import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  FormGroup,
  TextField,
  Slider,
  FormControl,
  IconButton
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Publish as PublishIcon,
  Edit as EditIcon,
  ContentCopy as DuplicateIcon
} from '@mui/icons-material';
import axios from 'axios';
import { buildProfileQuestionsMap, formatQuotaCriteriaSummary, formatQuotaDynamicConditions } from '../../utils/quotaDisplay';
import MatrixQuestion from '../../components/MatrixQuestion';

// Preview mode: show survey exactly like respondent (pagination + logic) but allow Skip/Abort controls for client/admin.

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Функция перевода категорий
const translateCategory = (category) => {
  const translations = {
    'online-surveys': 'Онлайн-опросы',
    'online-interviews': 'Онлайн-интервью и фокус-группы',
    'website-app-testing': 'Тест сайтов и приложений',
    'store-visits': 'Визиты в магазины и офисы',
    'product-testing': 'Тестирование продуктов и услуг',
    'other': 'Другое',
    // Старые категории для обратной совместимости
    'general': 'Онлайн-опросы',
    'market': 'Онлайн-опросы',
    'market-research': 'Онлайн-опросы',
  };
  return translations[category] || category;
};

export default function SurveyPreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [duplicating, setDuplicating] = useState(false);
  const [duplicateError, setDuplicateError] = useState('');
  // Profile questions map for quotas dynamic conditions
  const [profileQuestionsMap, setProfileQuestionsMap] = useState({});
  // Interactive preview states (like respondent view but no submission)
  const [interactive, setInteractive] = useState(false);
  const [answers, setAnswers] = useState({});
  const [questionFlow, setQuestionFlow] = useState([]);
  const [pageStartIndex, setPageStartIndex] = useState(0);
  const [pageStack, setPageStack] = useState([0]);
  const [attemptedNext, setAttemptedNext] = useState(false);
  const [pageSize, setPageSize] = useState(1);

  const findQuestionById = (qid) => survey?.questions?.find(q => q.id === qid) || null;

  const getSequentialNextId = (qid) => {
    if (!survey || !survey.questions) return null;
    const idx = survey.questions.findIndex(q => q.id === qid);
    if (idx === -1) return null;
    if (idx < survey.questions.length - 1) return survey.questions[idx + 1].id;
    return null;
  };

  const applyLogic = (questionId, answer) => {
    if (!survey || !Array.isArray(survey.logic) || survey.logic.length === 0) return null;
    if (Number(survey.questionsPerPage) === 5) return null; // Logic disabled for 5-per-page
    if (answer === undefined || answer === null || (typeof answer === 'string' && answer.trim() === '') || (Array.isArray(answer) && answer.length === 0)) {
      return null;
    }
    const rules = survey.logic.filter(l => l.questionId === questionId);
    for (const rule of rules) {
      const condition = rule.condition || {};
      const condAnswersRaw = Array.isArray(condition.answers) && condition.answers.length > 0
        ? condition.answers
        : (condition.answer ? [condition.answer] : []);
      const condAnswers = condAnswersRaw.map(a => (a == null ? '' : String(a).trim()));
      let conditionMet = false;
      if (typeof answer === 'string') {
        conditionMet = condAnswers.includes(String(answer).trim());
      } else if (Array.isArray(answer)) {
        const norm = answer.map(a => (a == null ? '' : String(a).trim()));
        conditionMet = norm.some(a => condAnswers.includes(a));
      }
      if (conditionMet) {
        return { action: condition.action, target: condition.target };
      }
    }
    return null;
  };

  const getNextQuestionIdByLogic = (qid) => {
    const answer = answers[qid];
    const result = applyLogic(qid, answer);
    if (!result) return getSequentialNextId(qid);
    if (result.action === 'end') return 'END';
    if (result.action === 'show' && result.target) return result.target;
    if (result.action === 'skip' && result.target) {
      const idx = survey.questions.findIndex(q => q.id === result.target);
      if (idx !== -1 && idx < survey.questions.length - 1) return survey.questions[idx + 1].id;
      return 'END';
    }
    return getSequentialNextId(qid);
  };

  const buildVisibleQuestions = () => {
    if (!survey || !survey.questions || questionFlow.length === 0) return { list: [], lastNext: null, pendingFlow: questionFlow };
    const result = [];
    let idx = pageStartIndex;
    let lastNext = null;
    let safety = 0;
    const localFlow = [...questionFlow];
    while (result.length < pageSize && idx < localFlow.length && safety < 200) {
      safety++;
      const qid = localFlow[idx];
      const q = findQuestionById(qid);
      if (!q) break;
      result.push(q);
      const nextId = getNextQuestionIdByLogic(qid);
      lastNext = nextId;
      if (!nextId || nextId === 'END') break;
      const existingIndex = localFlow.indexOf(nextId);
      if (existingIndex === -1) {
        localFlow.push(nextId);
        idx = localFlow.length - 1;
      } else {
        idx = existingIndex;
      }
    }
    return { list: result, lastNext, pendingFlow: localFlow };
  };

  const startInteractive = () => {
    if (!survey?.questions || survey.questions.length === 0) return;
    setPageSize(Number(survey.questionsPerPage) === 5 ? 5 : 1);
    setQuestionFlow([survey.questions[0].id]);
    setPageStartIndex(0);
    setPageStack([0]);
    setAnswers({});
    setAttemptedNext(false);
    setInteractive(true);
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleNext = () => {
    setAttemptedNext(true);
    const { list: visible, lastNext, pendingFlow } = buildVisibleQuestions();
    if (!visible || visible.length === 0) return;
    const hasUnanswered = visible.some(q => q.required && (answers[q.id] === undefined || answers[q.id] === null || (typeof answers[q.id] === 'string' && answers[q.id].trim() === '') || (Array.isArray(answers[q.id]) && answers[q.id].length === 0)));
    if (hasUnanswered) return;
    if (lastNext === 'END') {
      setInteractive(false);
      return;
    }
    if (pendingFlow.length !== questionFlow.length) setQuestionFlow(pendingFlow);
    if (visible.length > 0) {
      const lastId = visible[visible.length - 1].id;
      const nextId = getNextQuestionIdByLogic(lastId);
      if (!nextId || nextId === 'END') {
        setInteractive(false);
        return;
      }
      const existingIndex = pendingFlow.indexOf(nextId);
      if (existingIndex !== -1) {
        setPageStartIndex(existingIndex);
        setPageStack(prev => [...prev, existingIndex]);
        setAttemptedNext(false);
      } else {
        const updated = [...pendingFlow, nextId];
        setQuestionFlow(updated);
        const newIndex = updated.length - 1;
        setPageStartIndex(newIndex);
        setPageStack(ps => [...ps, newIndex]);
        setAttemptedNext(false);
      }
    }
  };

  const handleBack = () => {
    if (pageStack.length <= 1) return;
    const newStack = [...pageStack];
    newStack.pop();
    const newStart = newStack[newStack.length - 1] || 0;
    setPageStack(newStack);
    setPageStartIndex(newStart);
    setAttemptedNext(false);
  };

  const handleAbort = () => {
    setInteractive(false);
  };

  const handleSkipPage = () => {
    handleNext();
  };

  const renderPreviewInput = (question, answer) => {
    const SERVER_BASE = API_BASE.replace('/api', '');
    
    switch (question.type) {
      case 'single':
        const customSingleKey = '__custom__';
        const isCustomSingle = answer && !question.options.includes(answer);
        const displayValueSingle = isCustomSingle ? customSingleKey : (answer || '');
        
        return (
          <Box>
            <RadioGroup 
              value={displayValueSingle} 
              onChange={(e) => {
                if (e.target.value === customSingleKey) {
                  handleAnswerChange(question.id, '');
                } else {
                  handleAnswerChange(question.id, e.target.value);
                }
              }}
            >
              {question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  value={option}
                  control={<Radio />}
                  label={option}
                />
              ))}
              {question.allowCustomOption && (
                <FormControlLabel
                  value={customSingleKey}
                  control={<Radio />}
                  label="Свой вариант"
                />
              )}
            </RadioGroup>
            {question.allowCustomOption && (displayValueSingle === customSingleKey || isCustomSingle) && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={isCustomSingle ? answer : ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                sx={{ mt: 1, ml: 4 }}
              />
            )}
          </Box>
        );

      case 'multiple':
        const customMultipleKey = '__custom__';
        const answerArray = Array.isArray(answer) ? answer : [];
        const customValues = answerArray.filter(a => !question.options.includes(a));
        const hasCustom = customValues.length > 0;
        
        return (
          <Box>
            <FormGroup>
              {question.options.map((option, index) => (
                <FormControlLabel
                  key={index}
                  control={
                    <Checkbox
                      checked={answerArray.includes(option)}
                      onChange={(e) => {
                        const newAnswer = [...answerArray.filter(a => question.options.includes(a) || a === customValues[0])];
                        if (e.target.checked) {
                          newAnswer.push(option);
                        } else {
                          const idx = newAnswer.indexOf(option);
                          if (idx > -1) newAnswer.splice(idx, 1);
                        }
                        handleAnswerChange(question.id, newAnswer);
                      }}
                    />
                  }
                  label={option}
                />
              ))}
              {question.allowCustomOption && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasCustom}
                      onChange={(e) => {
                        if (!e.target.checked) {
                          const newAnswer = answerArray.filter(a => question.options.includes(a));
                          handleAnswerChange(question.id, newAnswer);
                        }
                      }}
                    />
                  }
                  label="Свой вариант"
                />
              )}
            </FormGroup>
            {question.allowCustomOption && hasCustom && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={customValues[0] || ''}
                onChange={(e) => {
                  const newAnswer = [...answerArray.filter(a => question.options.includes(a))];
                  if (e.target.value.trim()) {
                    newAnswer.push(e.target.value);
                  }
                  handleAnswerChange(question.id, newAnswer);
                }}
                sx={{ mt: 1, ml: 4 }}
              />
            )}
          </Box>
        );

      case 'text':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={answer || ''}
            onChange={(e) => handleAnswerChange(question.id, e.target.value)}
            placeholder="Введите ваш ответ"
          />
        );

      case 'scale':
        return (
          <Box sx={{ px: 2 }}>
            <Slider
              value={typeof answer === 'number' ? answer : (typeof question.scaleMin === 'number' ? question.scaleMin : 0)}
              onChange={(e, newValue) => handleAnswerChange(question.id, newValue)}
              min={typeof question.scaleMin === 'number' ? question.scaleMin : 0}
              max={typeof question.scaleMax === 'number' ? question.scaleMax : 10}
              marks
              step={1}
              valueLabelDisplay="on"
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Typography variant="caption">{typeof question.scaleMin === 'number' ? question.scaleMin : 0}</Typography>
              <Typography variant="caption">{typeof question.scaleMax === 'number' ? question.scaleMax : 10}</Typography>
            </Box>
          </Box>
        );

      case 'dropdown':
        const customDropdownKey = '__custom__';
        const isCustomDropdown = answer && !question.options.includes(answer);
        const displayValueDropdown = isCustomDropdown ? customDropdownKey : (answer || '');
        
        return (
          <Box>
            <FormControl fullWidth>
              <Typography variant="body2" sx={{ mb: 1 }}>Выберите один вариант</Typography>
              <RadioGroup
                value={displayValueDropdown}
                onChange={(e) => {
                  if (e.target.value === customDropdownKey) {
                    handleAnswerChange(question.id, '');
                  } else {
                    handleAnswerChange(question.id, e.target.value);
                  }
                }}
              >
                {(question.options || []).map((opt, idx) => (
                  <FormControlLabel key={idx} value={opt} control={<Radio />} label={opt} />
                ))}
                {question.allowCustomOption && (
                  <FormControlLabel value={customDropdownKey} control={<Radio />} label="Свой вариант" />
                )}
              </RadioGroup>
            </FormControl>
            {question.allowCustomOption && (displayValueDropdown === customDropdownKey || isCustomDropdown) && (
              <TextField
                fullWidth
                size="small"
                placeholder="Введите свой вариант"
                value={isCustomDropdown ? answer : ''}
                onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        );

      case 'likert':
        return (
          <RadioGroup value={answer || ''} onChange={(e)=>handleAnswerChange(question.id, e.target.value)}>
            {(question.likertOptions || [
              'Полностью согласен',
              'Скорее согласен',
              'Нейтрально',
              'Скорее не согласен',
              'Полностью не согласен'
            ]).map((opt, idx)=>(
              <FormControlLabel key={idx} value={opt} control={<Radio />} label={opt} />
            ))}
          </RadioGroup>
        );

      case 'nps':
        return (
          <Box sx={{ px:2 }}>
            <Typography variant="body2" sx={{ mb:1 }}>Насколько вероятно, что вы порекомендуете? (0-10)</Typography>
            <Slider
              value={typeof answer === 'number' ? answer : 0}
              onChange={(e, v) => handleAnswerChange(question.id, v)}
              min={0}
              max={10}
              marks
              step={1}
              valueLabelDisplay="on"
            />
            <Box sx={{ display:'flex', justifyContent:'space-between', mt:1 }}>
              <Typography variant="caption">0 (не буду)</Typography>
              <Typography variant="caption">10 (обязательно)</Typography>
            </Box>
          </Box>
        );

      case 'matrix':
        return (
          <MatrixQuestion
            rows={question.matrixRows || []}
            columns={question.matrixCols || []}
            value={answer || {}}
            onChange={(next) => handleAnswerChange(question.id, next)}
          />
        );

      case 'ranking':
        return (
          <Box>
            <Typography variant="body2" sx={{ mb:1 }}>Упорядочите варианты (перемещайте вверх/вниз)</Typography>
            {(() => {
              const current = Array.isArray(answer) && answer.length === (question.options || []).length
                ? answer
                : (question.options || []);
              const move = (idx, dir) => {
                const copy = [...current];
                const ni = dir === 'up' ? idx - 1 : idx + 1;
                if (ni < 0 || ni >= copy.length) return;
                [copy[idx], copy[ni]] = [copy[ni], copy[idx]];
                handleAnswerChange(question.id, copy);
              };
              return current.map((opt, idx) => (
                <Box key={opt+idx} sx={{ display:'flex', alignItems:'center', mb:1, gap:1 }}>
                  <Paper sx={{ flex:1, p:1 }} variant="outlined">{idx+1}. {opt}</Paper>
                  <IconButton size="small" onClick={() => move(idx,'up')} disabled={idx===0}>↑</IconButton>
                  <IconButton size="small" onClick={() => move(idx,'down')} disabled={idx===current.length-1}>↓</IconButton>
                </Box>
              ));
            })()}
          </Box>
        );

      default:
        return <Typography color="text.secondary">Предварительный просмотр для типа "{question.type}" недоступен</Typography>;
    }
  };

  useEffect(() => {
    fetchSurvey();
    fetchProfileQuestions();
  }, [id]);

  const fetchSurvey = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/surveys/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurvey(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching survey:', err);
      setError(err.response?.data?.error || 'Failed to load survey');
    } finally {
      setLoading(false);
    }
  };

  const fetchProfileQuestions = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/surveys/profile/questions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data?.questions || [];
      setProfileQuestionsMap(buildProfileQuestionsMap(list));
    } catch (err) {
      console.warn('Failed to fetch profile questions for quotas preview:', err?.response?.data || err.message);
    }
  };

  const handlePublish = async () => {
    try {
      setPublishing(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/surveys/${id}/publish`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      navigate('/client/dashboard');
    } catch (err) {
      console.error('Error publishing survey:', err);
      setError(err.response?.data?.error || 'Failed to publish survey');
    } finally {
      setPublishing(false);
    }
  };

  const handleDuplicate = async () => {
    if (duplicating) return;
    setDuplicating(true);
    setDuplicateError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_BASE}/surveys/${id}/duplicate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!data || !data.id) throw new Error('No id in duplicate response');
      navigate(`/client/survey-builder/${data.id}`, {
        state: { duplicatedFrom: id, duplicatedTitle: survey?.title }
      });
    } catch (err) {
      console.error('Error duplicating survey:', err);
      setDuplicateError(err.response?.data?.error || 'Не удалось дублировать опрос. Попробуйте ещё раз.');
      setDuplicating(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
        <Typography>Загрузка...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!survey) {
    return (
      <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
        <Alert severity="warning">Опрос не найден</Alert>
      </Container>
    );
  }

  const { list: visibleQuestions, lastNext } = interactive ? buildVisibleQuestions() : { list: [], lastNext: null };
  const isLastPage = interactive && (!lastNext || lastNext === 'END');
  const progressPct = interactive && survey?.questions?.length
    ? Math.min(100, Math.round(Object.keys(answers).length / survey.questions.length * 100))
    : 0;

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 0, sm: 2 } }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, md: 3 },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}
      >
        {duplicateError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDuplicateError('')}>
            {duplicateError}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Button 
            startIcon={<BackIcon />} 
            onClick={() => navigate('/client/dashboard')}
            sx={{
              color: '#718096',
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: '12px',
              '&:hover': {
                backgroundColor: 'rgba(113, 128, 150, 0.1)'
              }
            }}
          >
            Назад
          </Button>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {(survey.status === 'draft' || survey.status === 'rejected') ? (
              <Button 
                startIcon={<EditIcon />} 
                onClick={() => navigate(`/client/survey-builder/${id}`)} 
                sx={{
                  color: '#0984e3',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'rgba(9, 132, 227, 0.08)'
                  }
                }}
              >
                Редактировать
              </Button>
            ) : (
              <Alert 
                severity="info" 
                sx={{ 
                  py: 0.5,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.15) 0%, rgba(9, 132, 227, 0.15) 100%)',
                  border: '1px solid rgba(9, 132, 227, 0.3)',
                  '& .MuiAlert-icon': {
                    color: '#0984e3'
                  }
                }}
              >
                {survey.status === 'pending_moderation' && 'Опрос на модерации. Редактирование будет доступно после проверки или отклонения.'}
                {survey.status === 'active' && 'Опрос запущен. Редактирование недоступно.'}
                {survey.status === 'completed' && 'Опрос завершён. Редактирование недоступно.'}
              </Alert>
            )}
            {!survey.isProfileSurvey && (
              <Button
                startIcon={<DuplicateIcon />}
                onClick={handleDuplicate}
                disabled={duplicating}
                sx={{
                  color: '#00b894',
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: '12px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 184, 148, 0.08)'
                  }
                }}
              >
                Дублировать
              </Button>
            )}
            {survey.status === 'draft' && (
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={handlePublish}
                disabled={publishing}
                sx={{
                  background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  borderRadius: '12px',
                  textTransform: 'none',
                  px: 3,
                  boxShadow: '0 4px 14px rgba(45, 122, 31, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #2d7a1f 0%, #56ab2f 100%)',
                    boxShadow: '0 6px 20px rgba(45, 122, 31, 0.6)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: 'linear-gradient(135deg, #b2bec3 0%, #636e72 100%)',
                    color: '#fff',
                    opacity: 0.6
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Опубликовать
              </Button>
            )}
          </Box>
        </Box>

        <Typography 
          variant="h4" 
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#2d3748',
            fontSize: { xs: '1.5rem', md: '2.125rem' }
          }}
        >
          {survey.title}
        </Typography>
        <Typography 
          variant="body1" 
          paragraph
          sx={{
            color: '#718096',
            fontWeight: 500,
            lineHeight: 1.7
          }}
        >
          {survey.description}
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Chip 
              label={
                survey.status === 'active' ? 'Активен' : 
                survey.status === 'draft' ? 'Черновик' : 
                survey.status === 'pending_moderation' ? 'На модерации' : 
                survey.status === 'rejected' ? 'Отклонён' : 
                survey.status === 'completed' ? 'Завершён' : 
                survey.status
              } 
              sx={{
                background: 
                  survey.status === 'active' ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' :
                  survey.status === 'pending_moderation' ? 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' :
                  survey.status === 'draft' ? 'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)' :
                  survey.status === 'rejected' ? 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)' :
                  survey.status === 'completed' ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' :
                  'linear-gradient(135deg, #b2bec3 0%, #636e72 100%)',
                color: (survey.status === 'pending_moderation' || survey.status === 'draft') ? '#2d3748' : '#fff',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2
              }}
            />
          </Grid>
          <Grid item>
            <Chip 
              label={`Категория: ${translateCategory(survey.category)}`} 
              sx={{
                background: 'linear-gradient(135deg, rgba(162, 155, 254, 0.3) 0%, rgba(108, 92, 231, 0.3) 100%)',
                color: '#2d3748',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2
              }}
            />
          </Grid>
          <Grid item>
            <Chip 
              label={`~${survey.estimatedTime} мин`} 
              sx={{
                background: 'linear-gradient(135deg, rgba(253, 203, 110, 0.3) 0%, rgba(253, 203, 110, 0.5) 100%)',
                color: '#2d3748',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2
              }}
            />
          </Grid>
          <Grid item>
            <Chip 
              label={`${survey.reward?.amount || 0} BYN`} 
              sx={{
                background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                color: '#fff',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2
              }}
            />
          </Grid>
        </Grid>

        {survey.status === 'rejected' && survey.rejectionReason && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.15) 0%, rgba(214, 48, 49, 0.15) 100%)',
              border: '1px solid rgba(214, 48, 49, 0.3)',
              '& .MuiAlert-icon': {
                color: '#d63031'
              }
            }}
          >
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 700, color: '#d63031' }}>
              Причина отклонения:
            </Typography>
            <Typography variant="body2" sx={{ color: '#2d3748', fontWeight: 500 }}>
              {survey.rejectionReason}
            </Typography>
          </Alert>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Target Audience hidden per new requirements */}
        {!interactive && (
          <>
            <Typography 
              variant="h6" 
              gutterBottom
              sx={{
                fontWeight: 700,
                color: '#2d3748',
                fontSize: '1.25rem'
              }}
            >
              Вопросы ({survey.questions?.length || 0})
            </Typography>
            {survey.questions && survey.questions.map((q, index) => (
              <Card 
                key={q.id} 
                sx={{ 
                  mb: 2,
                  background: 'rgba(255, 255, 255, 0.7)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: '16px',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 6px 24px rgba(0, 0, 0, 0.1)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                <CardContent>
                  <Typography 
                    variant="subtitle1" 
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: '#2d3748'
                    }}
                  >
                    {index + 1}. {q.text}
                    {q.required && <Typography component="span" color="error" sx={{ fontWeight: 700 }}> *</Typography>}
                  </Typography>
                  <Chip 
                    label={q.type} 
                    size="small" 
                    sx={{ 
                      mb: 1,
                      background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.75rem'
                    }} 
                  />
                  {(q.type === 'single' || q.type === 'multiple') && q.options && (
                    <Box sx={{ mt: 1 }}>
                      {q.options.map((opt, i) => (
                        <Typography 
                          key={i} 
                          variant="body2" 
                          sx={{ 
                            color: '#718096',
                            fontWeight: 500,
                            ml: 2
                          }}
                        >
                          • {opt}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))}
            <Box sx={{ mt: 2, mb: 4 }}>
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.15) 0%, rgba(9, 132, 227, 0.15) 100%)',
                  border: '1px solid rgba(9, 132, 227, 0.3)',
                  '& .MuiAlert-icon': {
                    color: '#0984e3'
                  },
                  '& .MuiAlert-message': {
                    color: '#2d3748',
                    fontWeight: 500
                  }
                }}
              >
              </Alert>
              <Button 
                variant="contained" 
                onClick={startInteractive}
                sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  borderRadius: '12px',
                  textTransform: 'none',
                  px: 3,
                  py: 1.5,
                  boxShadow: '0 4px 14px rgba(9, 132, 227, 0.4)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)',
                    boxShadow: '0 6px 20px rgba(9, 132, 227, 0.6)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Интерактивный просмотр
              </Button>
            </Box>
          </>
        )}

        {interactive && (
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display:'flex', justifyContent:'space-between', mb:2 }}>
              <Typography variant="subtitle1">Страница {pageStack.length}</Typography>
              <Typography variant="subtitle2">Прогресс: {progressPct}%</Typography>
            </Box>
            {(visibleQuestions || []).map((q) => {
              const answer = answers[q.id];
              const isEmpty = q.required && (answer === undefined || answer === null || (typeof answer === 'string' && answer.trim() === '') || (Array.isArray(answer) && answer.length === 0));
              const showError = attemptedNext && isEmpty;
              return (
                <Card key={q.id} variant="outlined" sx={{ mb:3, borderColor: showError ? 'error.main' : undefined }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {q.text}{q.required && <Typography component="span" color="error"> *</Typography>}
                    </Typography>
                    {renderPreviewInput(q, answer)}
                    {showError && (
                      <Alert severity="error" sx={{ mt: 2, bgcolor: '#ffebee', borderColor: '#f44336' }}>
                        <Typography variant="body2" sx={{ color: '#d32f2f' }} fontWeight="medium">
                          На этот вопрос обязательно нужно ответить
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            <Box sx={{ display:'flex', justifyContent:'space-between', gap:2 }}>
              <Button onClick={handleBack} disabled={pageStack.length <= 1}>Назад</Button>
              <Box sx={{ display:'flex', gap:1 }}>
                <Button color="warning" onClick={handleSkipPage}>Пропустить страницу</Button>
                <Button color="error" onClick={handleAbort}>Прервать просмотр</Button>
                <Button variant="contained" onClick={handleNext}>{isLastPage ? 'Завершить' : 'Далее'}</Button>
              </Box>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        {/* Logic & Branching (static section) */}
        {survey.logic && survey.logic.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom>
              Логика переходов
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Вопрос</TableCell>
                    <TableCell>Если ответ</TableCell>
                    <TableCell>Действие</TableCell>
                    <TableCell>Цель</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {survey.logic.map((rule, index) => {
                    const question = survey.questions.find(q => q.id === rule.questionId);
                    const targetQuestion = survey.questions.find(q => q.id === rule.condition.target);
                    const answersArr = Array.isArray(rule.condition?.answers) && rule.condition.answers.length > 0
                      ? rule.condition.answers
                      : (rule.condition?.answer ? [rule.condition.answer] : []);
                    const actionLabel = rule.condition.action === 'skip' ? 'Пропустить'
                      : rule.condition.action === 'show' ? 'Показать'
                      : 'Завершить';
                    return (
                      <TableRow key={index}>
                        <TableCell>{question?.text || 'N/A'}</TableCell>
                        <TableCell>{answersArr.join(', ')}</TableCell>
                        <TableCell>{actionLabel}</TableCell>
                        <TableCell>{rule.condition.action === 'end' ? 'Завершить' : (targetQuestion?.text || 'N/A')}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Quotas */}
        {survey.quotas && survey.quotas.length > 0 && (
          <>
            <Typography variant="h6" gutterBottom>
              Квоты
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Описание</TableCell>
                    <TableCell>Критерии</TableCell>
                    <TableCell>Динамические условия</TableCell>
                    <TableCell>Лимит</TableCell>
                    <TableCell>Заполнено</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {survey.quotas.map((quota, index) => (
                      <TableRow key={quota.id || index}>
                        <TableCell>{quota.description || '-'}</TableCell>
                        <TableCell>{formatQuotaCriteriaSummary(quota.criteria)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'pre-line' }}>
                          {formatQuotaDynamicConditions(quota.criteria, profileQuestionsMap)}
                        </TableCell>
                        <TableCell>{quota.limit}</TableCell>
                        <TableCell>{quota.filled || 0}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Итоговая стоимость не отображается для заказчика */}
      </Paper>
    </Container>
  );
}

// Input renderers for preview (simplified; no file upload). Placed after component to access within file.
function renderPreviewInput(question, answer) {
  if (!question) return null;
  switch (question.type) {
    case 'single':
      return (
        <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
          {(question.options || []).map(opt => (
            <Button
              key={opt}
              variant={answer === opt ? 'contained' : 'outlined'}
              size="small"
              onClick={() => question.onAnswer && question.onAnswer(opt)}
              sx={{ textAlign:'left' }}
            >{opt}</Button>
          ))}
        </Box>
      );
    case 'multiple':
      return (
        <Box sx={{ display:'flex', flexDirection:'column', gap:1 }}>
          {(question.options || []).map(opt => {
            const selected = Array.isArray(answer) && answer.includes(opt);
            return (
              <Button
                key={opt}
                variant={selected ? 'contained' : 'outlined'}
                size="small"
                onClick={() => question.onAnswer && question.onAnswer(selected ? (answer.filter(a => a !== opt)) : ([...(answer||[]), opt]))}
                sx={{ textAlign:'left' }}
              >{opt}</Button>
            );
          })}
        </Box>
      );
    case 'text':
      return <Alert severity="info" sx={{ mt:1 }}>Текстовый ответ (в режиме предпросмотра ввод отключён)</Alert>;
    case 'scale':
      return <Alert severity="info" sx={{ mt:1 }}>Шкала: от {typeof question.scaleMin === 'number' ? question.scaleMin : 0} до {typeof question.scaleMax === 'number' ? question.scaleMax : 10} — ввод отключён в предпросмотре</Alert>;
    case 'dropdown':
      return <Alert severity="info" sx={{ mt:1 }}>Выпадающий список — выбор отключён</Alert>;
    case 'ranking':
      return (
        <Box sx={{ mt:1 }}>
          <Alert severity="info" sx={{ mb:1 }}>Ранжирование — изменение порядка отключено</Alert>
          {(question.options || []).map((opt, idx)=>(
            <Paper key={idx} variant="outlined" sx={{ p:1, mb:0.5 }}>
              {idx+1}. {opt}
            </Paper>
          ))}
        </Box>
      );
    case 'matrix':
      return (
        <Box sx={{ mt: 1 }}>
          <Alert severity="info" sx={{ mb: 1 }}>Матрица — выбор оценок отключён</Alert>
          <MatrixQuestion
            rows={question.matrixRows || []}
            columns={question.matrixCols || []}
            readOnly
          />
        </Box>
      );
    case 'likert':
      return (
        <Box sx={{ mt:1 }}>
          <Alert severity="info" sx={{ mb:1 }}>Согласие/несогласие — выбор отключён</Alert>
          {(question.likertOptions || [
            'Полностью согласен',
            'Скорее согласен',
            'Нейтрально',
            'Скорее не согласен',
            'Полностью не согласен'
          ]).map((opt,i)=>(
            <Paper key={i} variant="outlined" sx={{ p:1, mb:0.5 }}>{opt}</Paper>
          ))}
        </Box>
      );
    case 'nps':
      return <Alert severity="info" sx={{ mt:1 }}>NPS (0-10) — ввод отключён</Alert>;
    case 'file':
      return <Alert severity="info" sx={{ mt:1 }}>Загрузка файла недоступна в предпросмотре</Alert>;
    case 'single-image':
    case 'multiple-image':
      return <Alert severity="info" sx={{ mt:1 }}>Варианты с изображениями — интерактивная отметка отключена</Alert>;
    default:
      return <Alert severity="warning" sx={{ mt:1 }}>Неизвестный тип вопроса</Alert>;
  }
}
